import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, Eye, MousePointerClick, RefreshCw, Download, FileText, FileJson, GitCompare } from 'lucide-react';
import SummaryCard from '../components/dashboard/SummaryCard';
import DateRangePicker from '../components/dashboard/DateRangePicker';
import RevenueTrendChart from '../components/revenue/RevenueTrendChart';
import EcpmChart from '../components/revenue/EcpmChart';
import BreakdownCard from '../components/revenue/BreakdownCard';
import ComparisonCard from '../components/comparison/ComparisonCard';
import ComparisonChart from '../components/comparison/ComparisonChart';
import TrendInsights from '../components/trend/TrendInsights';
import { analyticsService } from '../services/analyticsService';
import { syncService } from '../services/syncService';
import { getDefaultDateRange } from '../utils/config';
import { exportRevenueData } from '../utils/exportUtils';
import { Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * Revenue Overview Page
 * Displays comprehensive revenue data and breakdowns
 */
const Revenue = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [trends, setTrends] = useState(null);
  const [breakdowns, setBreakdowns] = useState({
    apps: [],
    adUnits: [],
    countries: [],
    adSources: [],
  });
  const [comparisonMode, setComparisonMode] = useState(false);
  const [comparisonData, setComparisonData] = useState(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);

  // Sync state
  const [syncing, setSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [syncError, setSyncError] = useState(null);

  // Date range state (defaults to current month, 1st to today)
  const [dateRange, setDateRange] = useState(() => getDefaultDateRange());

  // Account ID state (optional, defaults to user's default account)
  const [accountId, setAccountId] = useState(null);

  // Chart configuration state
  const [chartType, setChartType] = useState('line'); // 'line', 'bar', 'area'
  const [period, setPeriod] = useState('daily'); // 'daily', 'weekly', 'monthly'

  // Breakdown view state
  const [expandedBreakdowns, setExpandedBreakdowns] = useState({
    apps: false,
    adUnits: false,
    countries: false,
    adSources: false,
  });
  const [breakdownLimit, setBreakdownLimit] = useState({
    apps: 5,
    adUnits: 5,
    countries: 5,
    adSources: 5,
  });

  // Fetch all revenue data
  const fetchRevenueData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        start_date: dateRange.startDate,
        end_date: dateRange.endDate,
        period: period,
      };

      if (accountId) {
        params.account_id = accountId;
      }

      // Fetch data in parallel
      const [summaryRes, chartRes, trendsRes, appsRes, adUnitsRes, countriesRes, adSourcesRes] = await Promise.all([
        analyticsService.getRevenueSummary(params).catch(() => ({ data: null })),
        analyticsService.getRevenueChart(params).catch(() => ({ data: null })),
        analyticsService.getRevenueTrends(params).catch(() => ({ data: null })),
        analyticsService.getRevenueBreakdownByApp({ ...params, limit: 0 }).catch(() => ({ data: null })),
        analyticsService.getRevenueBreakdownByAdUnit({ ...params, limit: 0 }).catch(() => ({ data: null })),
        analyticsService.getRevenueBreakdownByCountry({ ...params, limit: 0 }).catch(() => ({ data: null })),
        analyticsService.getRevenueBreakdownByAdSource({ ...params, limit: 0 }).catch(() => ({ data: null })),
      ]);

      if (summaryRes.data) {
        setSummary(summaryRes.data);
      }
      if (chartRes.data) {
        setChartData(chartRes.data);
      }
      if (trendsRes.data) {
        setTrends(trendsRes.data);
      }
      if (appsRes.data) {
        setBreakdowns(prev => ({ ...prev, apps: appsRes.data.data || [] }));
      }
      if (adUnitsRes.data) {
        setBreakdowns(prev => ({ ...prev, adUnits: adUnitsRes.data.data || [] }));
      }
      if (countriesRes.data) {
        setBreakdowns(prev => ({ ...prev, countries: countriesRes.data.data || [] }));
      }
      if (adSourcesRes.data) {
        setBreakdowns(prev => ({ ...prev, adSources: adSourcesRes.data.data || [] }));
      }
    } catch (err) {
      console.error('Error fetching revenue data:', err);
      setError(err.toString());
    } finally {
      setLoading(false);
    }
  };

  // Fetch comparison data
  const fetchComparisonData = async () => {
    if (!comparisonMode) {
      setComparisonData(null);
      return;
    }

    try {
      setComparisonLoading(true);

      // Calculate previous period dates (same duration as current period)
      const startDate = new Date(dateRange.startDate);
      const endDate = new Date(dateRange.endDate);
      const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));

      const previousEndDate = new Date(startDate);
      previousEndDate.setDate(previousEndDate.getDate() - 1);
      const previousStartDate = new Date(previousEndDate);
      previousStartDate.setDate(previousStartDate.getDate() - daysDiff);

      const params = {
        current_start_date: dateRange.startDate,
        current_end_date: dateRange.endDate,
        previous_start_date: previousStartDate.toISOString().split('T')[0],
        previous_end_date: previousEndDate.toISOString().split('T')[0],
      };

      if (accountId) {
        params.account_id = accountId;
      }

      const response = await analyticsService.getComparison(params);
      if (response.data) {
        setComparisonData(response.data);
      }
    } catch (err) {
      console.error('Error fetching comparison data:', err);
    } finally {
      setComparisonLoading(false);
    }
  };

  // Check last sync time and load it
  const loadSyncStatus = async () => {
    try {
      const syncStatusRes = await syncService.getSyncStatus(accountId, 'revenue');
      if (syncStatusRes.data?.latest?.revenue) {
        const lastSync = syncStatusRes.data.latest.revenue;
        const syncTime = new Date(lastSync.completed_at || lastSync.started_at);
        setLastSyncTime(syncTime);
        return syncTime;
      } else if (syncStatusRes.data?.statistics?.by_type?.revenue?.last_successful_sync) {
        // Fallback to statistics
        const syncTime = new Date(syncStatusRes.data.statistics.by_type.revenue.last_successful_sync);
        setLastSyncTime(syncTime);
        return syncTime;
      }
      return null;
    } catch (err) {
      console.error('Error loading sync status:', err);
      return null;
    }
  };

  // Manual sync handler
  const handleSync = async () => {
    try {
      setSyncing(true);
      setSyncError(null);

      const result = await syncService.fetchHistoricalData(
        dateRange.startDate,
        dateRange.endDate,
        accountId
      );

      if (result.data?.sync_result) {
        // Update last sync time and refresh data
        setLastSyncTime(new Date());
        // Refresh sync status to get accurate time
        await loadSyncStatus();
        // Refresh data after sync
        await fetchRevenueData();
      }
    } catch (err) {
      console.error('Sync error:', err);
      setSyncError(err.message || 'Failed to sync data');
    } finally {
      setSyncing(false);
    }
  };

  // On page load: Check sync status and auto-sync if data is older than 15 minutes
  useEffect(() => {
    const initializeSync = async () => {
      try {
        // Load sync status first
        const syncTime = await loadSyncStatus();

        // Auto-sync if data is older than 15 minutes
        if (syncTime) {
          const minutesSinceSync = (Date.now() - syncTime.getTime()) / (1000 * 60);
          if (minutesSinceSync > 15) {
            console.log(`Data is ${Math.round(minutesSinceSync)} minutes old. Auto-syncing...`);
            await handleSync();
          }
        } else {
          // No sync history, sync now
          console.log('No sync history found. Syncing...');
          await handleSync();
        }
      } catch (err) {
        console.error('Error initializing sync:', err);
        // Don't block page load if sync check fails
      }
    };

    initializeSync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  useEffect(() => {
    fetchRevenueData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.startDate, dateRange.endDate, accountId, period]);

  useEffect(() => {
    fetchComparisonData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparisonMode, dateRange.startDate, dateRange.endDate, accountId]);

  const handleDateRangeChange = (newDateRange) => {
    setDateRange(newDateRange);
  };

  const handleRefresh = () => {
    fetchRevenueData();
  };

  // Export handlers
  const handleExportCSV = () => {
    exportRevenueData(
      {
        summary,
        chartData,
        trends,
        breakdowns,
      },
      'csv',
      {
        dateRange,
        accountId,
        period,
        chartType,
      }
    );
  };

  const handleExportJSON = () => {
    exportRevenueData(
      {
        summary,
        chartData,
        trends,
        breakdowns,
      },
      'json',
      {
        dateRange,
        accountId,
        period,
        chartType,
      }
    );
  };

  if (loading && !summary) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-x-hidden">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading revenue data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-x-hidden">
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive mb-4">{error}</p>
            <Button onClick={handleRefresh} variant="destructive">
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Calculate summary metrics
  const summaryData = summary?.summary || summary || {};
  const totalRevenue = summaryData?.total_revenue || trends?.summary?.total_revenue || 0;
  const totalImpressions = summaryData?.total_impressions || 0;
  const averageEcpm = summaryData?.average_ecpm || (totalImpressions > 0 ? (totalRevenue / totalImpressions) * 1000 : 0);
  const totalClicks = summaryData?.total_clicks || 0;
  const ctr = summaryData?.ctr || (totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0);

  // Get trend indicators
  const revenueTrend = trends?.summary?.overall_trend || 'stable';
  const revenueChange = trends?.summary?.total_revenue ?
    (trends.summary.total_revenue - (trends.summary.total_revenue * 0.95)) / (trends.summary.total_revenue * 0.95) * 100 : 0;

  // Format last sync time
  const formatLastSyncTime = () => {
    if (!lastSyncTime) return 'Never synced';
    const now = new Date();
    const diffMs = now - lastSyncTime;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 sm:mb-3 lg:mb-4 gap-2">
        <div>
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold mb-0.5">Revenue Overview</h1>
          <p className="text-muted-foreground text-xs">
            Comprehensive revenue analytics and breakdowns
            {lastSyncTime && (
              <span className="ml-2">
                • Data synced {formatLastSyncTime()}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <Button
            onClick={handleSync}
            variant="outline"
            size="sm"
            disabled={syncing || loading}
            title="Sync data from IronSource"
            className="text-xs"
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync Now'}
          </Button>
          {syncError && (
            <span className="text-xs text-destructive" title={syncError}>
              Sync failed
            </span>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={loading || !summary} className="text-xs">
                <Download className="w-3 h-3 mr-1" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleExportCSV}>
                <FileText className="w-3 h-3 mr-2" />
                Export as CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportJSON}>
                <FileJson className="w-3 h-3 mr-2" />
                Export as JSON
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={handleRefresh} variant="outline" size="sm" disabled={loading} className="text-xs">
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Date Range Picker & Comparison Toggle */}
      <Card className="mb-2 sm:mb-3 lg:mb-4">
        <CardHeader className="pb-2">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <CardTitle className="text-sm sm:text-base">Date Range</CardTitle>
            <div className="flex items-center gap-2">
              <Checkbox
                id="comparison-mode"
                checked={comparisonMode}
                onCheckedChange={setComparisonMode}
              />
              <label
                htmlFor="comparison-mode"
                className="text-xs sm:text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-1.5 cursor-pointer"
              >
                <GitCompare className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                Compare with Previous Period
              </label>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <DateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={handleDateRangeChange}
          />
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {comparisonMode && comparisonData ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4 mb-2 sm:mb-3 lg:mb-4">
          <ComparisonCard
            title="Total Revenue"
            currentValue={comparisonData.current_period?.metrics?.revenue || totalRevenue}
            previousValue={comparisonData.previous_period?.metrics?.revenue || 0}
            format="currency"
            icon={DollarSign}
            subtitle={`${dateRange.startDate} to ${dateRange.endDate}`}
          />
          <ComparisonCard
            title="Total Impressions"
            currentValue={comparisonData.current_period?.metrics?.impressions || totalImpressions}
            previousValue={comparisonData.previous_period?.metrics?.impressions || 0}
            format="number"
            icon={Eye}
            subtitle="Ad impressions"
          />
          <ComparisonCard
            title="Average eCPM"
            currentValue={comparisonData.current_period?.metrics?.ecpm || averageEcpm}
            previousValue={comparisonData.previous_period?.metrics?.ecpm || 0}
            format="currency"
            icon={TrendingUp}
            subtitle="Effective cost per mille"
          />
          <ComparisonCard
            title="Click-Through Rate"
            currentValue={comparisonData.current_period?.metrics?.ctr || ctr}
            previousValue={comparisonData.previous_period?.metrics?.ctr || 0}
            format="percentage"
            icon={MousePointerClick}
            subtitle={`${totalClicks.toLocaleString()} clicks`}
          />
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4 mb-2 sm:mb-3 lg:mb-4">
          <SummaryCard
            title="Total Revenue"
            value={totalRevenue}
            subtitle={`${dateRange.startDate} to ${dateRange.endDate}`}
            trend={revenueTrend === 'increasing' ? 'up' : revenueTrend === 'decreasing' ? 'down' : null}
            trendValue={revenueChange}
            icon={DollarSign}
            isCurrency={true}
          />
          <SummaryCard
            title="Total Impressions"
            value={totalImpressions}
            subtitle="Ad impressions"
            icon={Eye}
          />
          <SummaryCard
            title="Average eCPM"
            value={averageEcpm}
            subtitle="Effective cost per mille"
            icon={TrendingUp}
            isCurrency={true}
          />
          <SummaryCard
            title="Click-Through Rate"
            value={ctr}
            subtitle={`${totalClicks.toLocaleString()} clicks`}
            icon={MousePointerClick}
            isPercentage={true}
          />
        </div>
      )}

      {/* Revenue Charts Section */}
      <div className="space-y-2 sm:space-y-3 lg:space-y-4 mb-2 sm:mb-3 lg:mb-4">
        {comparisonMode && comparisonData && comparisonData.current_period && comparisonData.previous_period ? (
          <>
            <Card>
              <CardHeader>
                <CardTitle>Period Comparison</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3 lg:gap-4 mb-2 sm:mb-3 lg:mb-4">
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <div className="text-sm text-muted-foreground mb-1">Current Period</div>
                    <div className="text-lg font-semibold">
                      {dateRange.startDate} to {dateRange.endDate}
                    </div>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <div className="text-sm text-muted-foreground mb-1">Previous Period</div>
                    <div className="text-lg font-semibold">
                      {comparisonData.previous_period.start_date} to {comparisonData.previous_period.end_date}
                    </div>
                  </div>
                </div>
                {comparisonData.changes && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div className="text-center p-3 bg-background rounded-lg border">
                      <div className="text-xs text-muted-foreground mb-1">Revenue Change</div>
                      <div className={`text-lg font-bold ${comparisonData.changes.revenue?.percentage_change > 0 ? 'text-green-600' : comparisonData.changes.revenue?.percentage_change < 0 ? 'text-red-600' : ''}`}>
                        {comparisonData.changes.revenue?.percentage_change > 0 ? '+' : ''}{comparisonData.changes.revenue?.percentage_change?.toFixed(2) || 0}%
                      </div>
                    </div>
                    <div className="text-center p-3 bg-background rounded-lg border">
                      <div className="text-xs text-muted-foreground mb-1">Impressions Change</div>
                      <div className={`text-lg font-bold ${comparisonData.changes.impressions?.percentage_change > 0 ? 'text-green-600' : comparisonData.changes.impressions?.percentage_change < 0 ? 'text-red-600' : ''}`}>
                        {comparisonData.changes.impressions?.percentage_change > 0 ? '+' : ''}{comparisonData.changes.impressions?.percentage_change?.toFixed(2) || 0}%
                      </div>
                    </div>
                    <div className="text-center p-3 bg-background rounded-lg border">
                      <div className="text-xs text-muted-foreground mb-1">eCPM Change</div>
                      <div className={`text-lg font-bold ${comparisonData.changes.ecpm?.percentage_change > 0 ? 'text-green-600' : comparisonData.changes.ecpm?.percentage_change < 0 ? 'text-red-600' : ''}`}>
                        {comparisonData.changes.ecpm?.percentage_change > 0 ? '+' : ''}{comparisonData.changes.ecpm?.percentage_change?.toFixed(2) || 0}%
                      </div>
                    </div>
                    <div className="text-center p-3 bg-background rounded-lg border">
                      <div className="text-xs text-muted-foreground mb-1">CTR Change</div>
                      <div className={`text-lg font-bold ${comparisonData.changes.ctr?.percentage_change > 0 ? 'text-green-600' : comparisonData.changes.ctr?.percentage_change < 0 ? 'text-red-600' : ''}`}>
                        {comparisonData.changes.ctr?.percentage_change > 0 ? '+' : ''}{comparisonData.changes.ctr?.percentage_change?.toFixed(2) || 0}%
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            {trends && trends.summary && (
              <TrendInsights summary={trends.summary} metric="revenue" format="currency" />
            )}
            <RevenueTrendChart
              data={chartData?.data || []}
              trendData={trends}
              chartType={chartType}
              period={period}
              onChartTypeChange={setChartType}
              onPeriodChange={setPeriod}
            />
            <EcpmChart data={chartData?.data || []} />
          </>
        ) : (
          <>
            {trends && trends.summary && (
              <TrendInsights summary={trends.summary} metric="revenue" format="currency" />
            )}
            <RevenueTrendChart
              data={chartData?.data || []}
              trendData={trends}
              chartType={chartType}
              period={period}
              onChartTypeChange={setChartType}
              onPeriodChange={setPeriod}
            />
            <EcpmChart data={chartData?.data || []} />
          </>
        )}
      </div>

      {/* Breakdowns Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-3 lg:gap-4">
        <BreakdownCard
          title="Revenue by App"
          data={breakdowns.apps}
          expanded={expandedBreakdowns.apps}
          onToggleExpand={() => setExpandedBreakdowns(prev => ({ ...prev, apps: !prev.apps }))}
          chartType="pie"
          limit={breakdownLimit.apps}
        />
        <BreakdownCard
          title="Revenue by Ad Unit"
          data={breakdowns.adUnits}
          expanded={expandedBreakdowns.adUnits}
          onToggleExpand={() => setExpandedBreakdowns(prev => ({ ...prev, adUnits: !prev.adUnits }))}
          chartType="bar"
          limit={breakdownLimit.adUnits}
        />
        <BreakdownCard
          title="Revenue by Country"
          data={breakdowns.countries}
          expanded={expandedBreakdowns.countries}
          onToggleExpand={() => setExpandedBreakdowns(prev => ({ ...prev, countries: !prev.countries }))}
          limit={breakdownLimit.countries}
        />
        <BreakdownCard
          title="Revenue by Ad Source"
          data={breakdowns.adSources}
          expanded={expandedBreakdowns.adSources}
          onToggleExpand={() => setExpandedBreakdowns(prev => ({ ...prev, adSources: !prev.adSources }))}
          limit={breakdownLimit.adSources}
        />
      </div>
    </div>
  );
};

export default Revenue;
